import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { BillableItemCreate, BillableItemUpdate } from '@/lib/schemas';
import { toast } from 'sonner';
import { invalidateProjectListQueries } from '@/lib/cache-utils';
// 2026-08-07: every error toast in this file used to be
// `toast.error(error.message || '<fallback>')`, which shows a bookkeeper the raw
// browser string "Failed to fetch" the moment the network drops — caught by the
// offline e2e spec, which logged exactly that for the line-item amount save.
// mutationErrorToast maps no-status -> plain-language offline, 5xx -> transient,
// 4xx -> the server's own sentence, persistently.
import { mutationErrorToast } from '@/lib/globalErrorHandlers';
import { postSendAcknowledgementIds } from '@/lib/sharedItemEdit';

// ============================================================================
// Query Hooks
// ============================================================================

export function useBillableItems(
  params?: {
    project_id?: string;
    cancelled?: boolean; // Migration 013: Replaced status with cancelled
    unbilled_only?: boolean; // Migration 013: For Work to Bill view
    search?: string;
    created_after?: string;
    created_before?: string;
    limit?: number;
    offset?: number;
  },
  options?: { keepPreviousData?: boolean }
) {
  // If project_id is explicitly passed (even as undefined), wait for it to be defined
  // This prevents querying with ?project_id= which returns wrong results
  const shouldWaitForProjectId = params && 'project_id' in params;
  const isReady = !shouldWaitForProjectId || !!params?.project_id;

  return useQuery({
    queryKey: ['billable-items', params],
    queryFn: () => api.billableItems.list(params),
    enabled: isReady,
    placeholderData: options?.keepPreviousData ? (prev) => prev : undefined,
  });
}

export function useBillableItem(id: string | undefined) {
  return useQuery({
    queryKey: ['billable-items', id],
    queryFn: () => api.billableItems.get(id!),
    enabled: !!id,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateBillableItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BillableItemCreate) => api.billableItems.create(data),
    onSuccess: (response, variables) => {
      const newItem = response?.billable_item || response;
      // Update cache directly from response — no refetch race
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'billable-items' },
        (old) => {
          if (!old?.billable_items) return old;
          return {
            ...old,
            billable_items: [...old.billable_items, newItem],
            total: (old.total ?? 0) + 1,
          };
        }
      );
      // Only invalidate project LIST queries (for financial summary) — NOT billable-items
      // (we just set that cache directly, refetching would overwrite it)
      // CRITICAL: Exclude detail queries to prevent stale Cloud Run reads from
      // overwriting recent edits to project_understanding, fee_assumptions, etc.
      invalidateProjectListQueries(queryClient);
      toast.success('Billable item created successfully');
      return newItem;
    },
    onError: (error: any) => {
      mutationErrorToast(error, 'Failed to create billable item');
    },
  });
}

export function useUpdateBillableItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BillableItemUpdate }) =>
      api.billableItems.update(id, data),
    onSuccess: (response, variables) => {
      const updatedItem = response?.billable_item || response;
      // Update cache directly from response — no refetch race
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'billable-items' },
        (old) => {
          if (!old?.billable_items) return old;
          return {
            ...old,
            billable_items: old.billable_items.map((item: any) =>
              item.id === variables.id ? { ...item, ...updatedItem } : item
            ),
          };
        }
      );
      // Also invalidate project LIST queries to update financial summary
      // Exclude detail queries to prevent stale reads overwriting fresh cache
      invalidateProjectListQueries(queryClient);
      toast.success('Billable item updated successfully');
    },
    onError: (error: any) => {
      // A "sent since the page loaded" refusal is explained in the other-invoices list itself (InvoiceItemRow), in plain
      // words; a second, technical toast on top of it would only confuse.
      if (postSendAcknowledgementIds(error) !== null) return;
      mutationErrorToast(error, 'Failed to update billable item');
    },
  });
}

/**
 * Cancelled-item spec (R2): cancel / uncancel a billable item.
 *
 * On cancel→true the server has already (atomically) removed the item from any
 * draft/ready invoices and recalculated them, so we invalidate BOTH the
 * billable-items lists AND the invoices caches (the affected invoice's totals
 * and item list changed server-side). On uncancel→false there are no invoice
 * side effects, but the same invalidation is harmless and keeps the logic simple.
 *
 * The 409 (cancel_blocked_invoice_sent) and 422 paths surface as errors; the
 * caller (CancelBillableItemDialog) handles the block messaging, so we don't
 * toast a generic error for the 409 here.
 */
export function useSetBillableItemCancelled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, cancelled }: { id: string; cancelled: boolean }) =>
      api.billableItems.update(id, { cancelled }),
    onSuccess: (response, variables) => {
      const updatedItem = response?.billable_item || response;
      // Optimistically flip `cancelled` in the billable-items caches for instant feedback.
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'billable-items' },
        (old) => {
          if (!old?.billable_items) return old;
          return {
            ...old,
            billable_items: old.billable_items.map((item: any) =>
              item.id === variables.id ? { ...item, ...updatedItem } : item
            ),
          };
        }
      );
      // ...then refetch billable-items: cancelling removed the item from draft/ready
      // invoices server-side, so its calculated `invoiced_amount` and the table's
      // Total Invoiced changed — the optimistic patch (which only carries the
      // billable_items row, not the recomputed aggregate) can't know the new value,
      // leaving the project table stale until reload without this.
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'billable-items' });
      // The server may have removed this item from draft/ready invoices and
      // recalculated their totals — refetch invoices and invoice-items so the
      // affected invoice and any list reflect the new totals.
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'invoices' });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      invalidateProjectListQueries(queryClient);
      toast.success(
        variables.cancelled ? 'Billable item cancelled' : 'Billable item restored'
      );
    },
    onError: (error: any) => {
      // 409 (blocked by sent invoice) is handled by the dialog with a richer
      // message; only toast for genuinely unexpected failures.
      if (error?.status !== 409) {
        mutationErrorToast(error, 'Failed to update billable item');
      }
    },
  });
}

/**
 * 9.9d CONTRACT-02: Reorder billable items via drag-drop
 * Updates display_order for multiple items in sequence
 */
export function useReorderBillableItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: { id: string; display_order: number }[]) => {
      // Update each item's display_order in sequence
      // For now, use individual updates since there's no batch endpoint
      const updates = items.map(({ id, display_order }) =>
        api.billableItems.update(id, { display_order })
      );
      return Promise.all(updates);
    },
    onSuccess: () => {
      // Invalidate billable items queries to refresh with new order
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'billable-items'
      });
      toast.success('Items reordered');
    },
    onError: (error: any) => {
      mutationErrorToast(error, 'Failed to reorder items');
    },
  });
}

export function useDeleteBillableItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.billableItems.delete(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        predicate: (query) => query.queryKey[0] === 'billable-items'
      });

      // Snapshot the previous value for rollback
      const previousBillableItems = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === 'billable-items'
      });

      // Optimistically update all billable items queries
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'billable-items' },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            billable_items: old.billable_items.filter((item: any) => item.id !== id),
            total: old.total - 1, // Update total count for pagination
          };
        }
      );

      return { previousBillableItems };
    },
    onError: (error: any, id, context) => {
      // Rollback on error
      if (context?.previousBillableItems) {
        context.previousBillableItems.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      if (error.status === 400) {
        toast.error('Cannot delete billable item linked to invoices');
      } else {
        mutationErrorToast(error, 'Failed to delete billable item');
      }
    },
    onSuccess: () => {
      // Invalidate project LIST queries after successful delete (for financial summary)
      // Exclude detail queries to prevent stale reads overwriting fresh cache
      invalidateProjectListQueries(queryClient);
      toast.success('Billable item deleted successfully');
    },
  });
}
